import { type MaybeElementRef, unrefElement, useEventListener } from '@vueuse/core';
import type { MaybeRefOrGetter } from 'vue';

export type DismissReason = 'escape' | 'pointer-outside' | 'focus-outside';

export type UseDismissOptions = {
    /** The layer listens only while this is `true` */
    open: MaybeRefOrGetter<boolean>;
    /** Elements that count as inside the layer: the floating element, its trigger, its anchor */
    elements: MaybeRefOrGetter<readonly MaybeElementRef[]>;
    onDismiss: (reason: DismissReason, event: Event) => void;
    /** Dismiss when focus moves out of `elements`. Default `true` */
    focusOutside?: MaybeRefOrGetter<boolean>;
};

type Layer = { contains: (target: Node) => boolean };

/* Open layers, oldest first. A layer opened from inside another layer (a combobox in a dialog) stacks above it. */
const layers: Layer[] = [];

/**
 * Closes a floating layer (popover, listbox, menu) on Escape, on a pointer press outside it,
 * and on focus moving outside it.
 *
 * Layers stack: Escape closes only the topmost open layer, and a press inside a layer above
 * (a teleported listbox opened from a dialog) does not count as outside the layers below it.
 *
 * @example
 * useDismiss({
 *     open,
 *     elements: () => [anchor.value, floating.value],
 *     onDismiss: () => (open.value = false),
 * });
 */
export function useDismiss(options: UseDismissOptions): void {
    const contains = (target: Node) =>
        toValue(options.elements).some((element) => {
            const node = unrefElement(element);
            return node instanceof Node && node.contains(target);
        });
    const layer: Layer = { contains };

    const isTopmost = () => layers.at(-1) === layer;
    /* Inside this layer, or inside any layer stacked above it. */
    const isInside = (target: Node) => layers.slice(layers.indexOf(layer)).some((other) => other.contains(target));

    const remove = () => {
        const index = layers.indexOf(layer);
        if (index !== -1) layers.splice(index, 1);
    };

    watch(
        () => toValue(options.open),
        (open) => {
            remove();
            if (open) layers.push(layer);
        },
        { immediate: true },
    );
    onScopeDispose(remove);

    const isListening = () => toValue(options.open) && layers.includes(layer);

    useEventListener(document, 'keydown', (event: KeyboardEvent) => {
        if (event.key !== 'Escape' || event.defaultPrevented || !isListening() || !isTopmost()) return;
        event.preventDefault();
        options.onDismiss('escape', event);
    });

    useEventListener(
        document,
        'pointerdown',
        (event: PointerEvent) => {
            if (!isListening() || !(event.target instanceof Node) || isInside(event.target)) return;
            options.onDismiss('pointer-outside', event);
        },
        { capture: true },
    );

    useEventListener(document, 'focusin', (event: FocusEvent) => {
        if (toValue(options.focusOutside) === false || !isListening()) return;
        if (!(event.target instanceof Node) || isInside(event.target)) return;
        options.onDismiss('focus-outside', event);
    });
}
